/**
 * Marketing APIs page — SEO audit + Meta Ad Library + Google Ads keyword
 * opportunity. All three cards can be tested right here before keys arrive.
 *
 * MOCKED data is clearly labeled with a yellow "MOCK" pill so users don't
 * mistake it for production signal. Meta Ad Library uses real Apify data when
 * the Apify token is present.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp, Search, Facebook, DollarSign, Loader2, Check, X, AlertTriangle,
  Globe, Link as LinkIcon, Target, Gauge,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import DataForSEOConfigCard from "../components/DataForSEOConfigCard";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  pt: {
    title: "Marketing APIs",
    subtitle: "SEO · Meta Ads · Google Ads — audite leads antes de fechar a proposta.",
    status_title: "Status das integrações",
    status_connected: "Conectado", status_mocked: "MOCK",
    card_seo_title: "SEO Audit", card_seo_desc: "Domain authority, backlinks, issues e palavras-chave orgânicas para alimentar o AI Audit.",
    card_meta_title: "Meta Ad Library", card_meta_desc: "Descubra se o lead tem campanhas ativas no Facebook/Instagram agora.",
    card_ga_title: "Google Ads Keywords", card_ga_desc: "Volume de busca + CPC + oportunidade mensal não capturada.",
    run: "Rodar", domain_ph: "exemplo.com", query_ph: "Roofing Miami", location_ph: "Miami, FL",
    seo_da: "Domain Authority", seo_backlinks: "Backlinks", seo_kws: "Keywords orgânicas", seo_local: "Local SEO",
    seo_issues: "Problemas detectados", seo_top_kws: "Top keywords",
    meta_active_yes: "Anunciando agora", meta_active_no: "Sem ads ativos",
    meta_open: "Oportunidade: ofereça tráfego pago", meta_sample: "Exemplos de criativos",
    ga_search_vol: "Buscas/mês", ga_cpc: "CPC médio", ga_opp: "Oportunidade de tráfego (mês)",
    ga_table_kw: "Keyword", ga_table_vol: "Volume", ga_table_comp: "Comp.", ga_table_cpc: "CPC",
    mock_notice: "Dados MOCKADOS — conecte sua API real (Semrush / Ahrefs / GSC) para substituir.",
  },
  en: {
    title: "Marketing APIs",
    subtitle: "SEO · Meta Ads · Google Ads — audit leads before you pitch.",
    status_title: "Integration status",
    status_connected: "Connected", status_mocked: "MOCK",
    card_seo_title: "SEO Audit", card_seo_desc: "Domain authority, backlinks, issues and organic keywords to power the AI Audit.",
    card_meta_title: "Meta Ad Library", card_meta_desc: "Find out if the lead is running ads on Facebook/Instagram right now.",
    card_ga_title: "Google Ads Keywords", card_ga_desc: "Search volume + CPC + monthly traffic opportunity they're leaving on the table.",
    run: "Run", domain_ph: "example.com", query_ph: "Roofing Miami", location_ph: "Miami, FL",
    seo_da: "Domain Authority", seo_backlinks: "Backlinks", seo_kws: "Organic keywords", seo_local: "Local SEO",
    seo_issues: "Detected issues", seo_top_kws: "Top keywords",
    meta_active_yes: "Advertising now", meta_active_no: "No active ads",
    meta_open: "Opportunity: pitch paid traffic", meta_sample: "Sample creatives",
    ga_search_vol: "Monthly searches", ga_cpc: "Avg CPC", ga_opp: "Traffic opportunity (month)",
    ga_table_kw: "Keyword", ga_table_vol: "Volume", ga_table_comp: "Comp.", ga_table_cpc: "CPC",
    mock_notice: "MOCKED data — hook a real API (Semrush / Ahrefs / GSC) to replace.",
  },
  es: {
    title: "APIs de Marketing",
    subtitle: "SEO · Meta Ads · Google Ads — audita leads antes de proponer.",
    status_title: "Estado de las integraciones",
    status_connected: "Conectado", status_mocked: "MOCK",
    card_seo_title: "Auditoría SEO", card_seo_desc: "Domain authority, backlinks, errores y keywords orgánicas para alimentar el AI Audit.",
    card_meta_title: "Biblioteca de Anuncios Meta", card_meta_desc: "Descubre si el lead está anunciando en Facebook/Instagram ahora.",
    card_ga_title: "Keywords de Google Ads", card_ga_desc: "Volumen + CPC + oportunidad mensual que están dejando sobre la mesa.",
    run: "Ejecutar", domain_ph: "ejemplo.com", query_ph: "Techos Miami", location_ph: "Miami, FL",
    seo_da: "Domain Authority", seo_backlinks: "Backlinks", seo_kws: "Keywords orgánicas", seo_local: "Local SEO",
    seo_issues: "Problemas detectados", seo_top_kws: "Top keywords",
    meta_active_yes: "Anunciando ahora", meta_active_no: "Sin anuncios activos",
    meta_open: "Oportunidad: ofrece tráfico pago", meta_sample: "Ejemplos de creativos",
    ga_search_vol: "Búsquedas/mes", ga_cpc: "CPC promedio", ga_opp: "Oportunidad de tráfico (mes)",
    ga_table_kw: "Keyword", ga_table_vol: "Volumen", ga_table_comp: "Comp.", ga_table_cpc: "CPC",
    mock_notice: "Datos MOCKEADOS — conecta tu API real (Semrush / Ahrefs / GSC) para reemplazar.",
  },
};

function StatusPill({ on, t, lang }) {
  if (on) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 font-bold">
        <Check className="w-3 h-3" /> {t.status_connected}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 font-bold"
          data-testid="status-ready">
      <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
      {lang === "pt" ? "Pronta para operação" : "Ready to operate"}
    </span>
  );
}

/**
 * Central registry mapping every integration row on this page to a
 * configure/help destination. Clicking the row takes the user straight
 * there instead of leaving them wondering where to plug the key.
 *
 * • "internal" targets scroll to an inline form on this page.
 * • "app" targets navigate to another page in the app.
 * • "external" targets open the provider's dashboard in a new tab.
 */
const INTEGRATION_TARGETS = {
  dataforseo: {
    kind: "internal", anchor: "dataforseo-config",
    label_pt: "Configurar DataForSEO abaixo",
    label_en: "Configure DataForSEO below",
    docs: "https://app.dataforseo.com/api-access",
  },
  semrush: {
    kind: "external", url: "https://www.semrush.com/api/",
    label_pt: "Obter chave Semrush",
    label_en: "Get Semrush API key",
  },
  ahrefs: {
    kind: "external", url: "https://ahrefs.com/api",
    label_pt: "Obter chave Ahrefs",
    label_en: "Get Ahrefs API key",
  },
  gsc: {
    kind: "external", url: "https://search.google.com/search-console",
    label_pt: "Conectar Google Search Console",
    label_en: "Connect Google Search Console",
  },
  meta_graph_api: {
    kind: "external", url: "https://developers.facebook.com/apps",
    label_pt: "Criar app Meta + token",
    label_en: "Create Meta app + token",
  },
  meta_ad_library_apify: {
    kind: "app", path: "/marketplace",
    label_pt: "Configurar token Apify",
    label_en: "Configure Apify token",
  },
  google_ads_dataforseo: {
    kind: "internal", anchor: "dataforseo-config",
    label_pt: "Usa a mesma chave DataForSEO",
    label_en: "Uses the DataForSEO key",
  },
  google_ads_developer_token: {
    kind: "external",
    url: "https://developers.google.com/google-ads/api/docs/first-call/dev-token",
    label_pt: "Solicitar Developer Token",
    label_en: "Request Developer Token",
  },
  google_ads_refresh_token: {
    kind: "external",
    url: "https://developers.google.com/google-ads/api/docs/first-call/oauth-cloud-project",
    label_pt: "Gerar Refresh Token (OAuth)",
    label_en: "Generate OAuth Refresh Token",
  },
  // WhatsApp / conversational marketing providers (Iter66) — every entry
  // below is an officially BSP-certified WhatsApp Business API reseller.
  wati: {
    kind: "external", url: "https://app.wati.io/register",
    label_pt: "Criar conta Wati",
    label_en: "Create Wati account",
  },
  respond_io: {
    kind: "external", url: "https://www.respond.io/sign-up",
    label_pt: "Criar conta respond.io",
    label_en: "Create respond.io account",
  },
  sleekflow: {
    kind: "external", url: "https://app.sleekflow.io/sign-up",
    label_pt: "Criar conta SleekFlow",
    label_en: "Create SleekFlow account",
  },
  blip_go: {
    kind: "external", url: "https://www.blip.ai/blip-go/",
    label_pt: "Criar conta Blip Go",
    label_en: "Create Blip Go account",
  },
};

function ClickableIntegrationRow({ providerKey, label, connected, t, lang, navigate }) {
  const target = INTEGRATION_TARGETS[providerKey];
  const actionLabel = target && (lang === "pt" ? target.label_pt : target.label_en);
  const handleClick = () => {
    if (!target) return;
    if (target.kind === "internal") {
      const el = document.getElementById(target.anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else if (target.kind === "app") {
      navigate(target.path);
    } else if (target.kind === "external") {
      window.open(target.url, "_blank", "noopener,noreferrer");
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={`mkt-integration-row-${providerKey}`}
      className="w-full group flex items-center justify-between gap-2 mt-1 -mx-1 px-1 py-0.5 rounded hover:bg-[var(--al-bg-elev)] transition-colors cursor-pointer text-left"
      title={actionLabel}
    >
      <span className="flex items-center gap-1.5">
        <span className="group-hover:text-[#8B5CF6] transition-colors">{label}</span>
        <StatusPill on={connected} t={t} lang={lang} />
      </span>
      <span className="text-[10px] text-[var(--al-text-muted)] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap flex items-center gap-1">
        {connected ? (lang === "pt" ? "Gerenciar" : "Manage") : actionLabel} →
      </span>
    </button>
  );
}

export default function MarketingAPIs() {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.en;
  const [status, setStatus] = useState(null);
  const [seoDomain, setSeoDomain] = useState("");
  const [seoData, setSeoData] = useState(null);
  const [seoLoading, setSeoLoading] = useState(false);
  const [metaQ, setMetaQ] = useState("");
  const [metaData, setMetaData] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [gaQuery, setGaQuery] = useState("");
  const [gaLocation, setGaLocation] = useState("Miami, FL");
  const [gaData, setGaData] = useState(null);
  const [gaLoading, setGaLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    refreshStatus();
  }, []);

  const refreshStatus = useCallback(() => {
    axios.get(`${API}/marketing-apis/status`).then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  const runSeo = async () => {
    if (!seoDomain) return;
    setSeoLoading(true);
    try {
      const { data } = await axios.get(`${API}/marketing-apis/seo/audit`, { params: { domain: seoDomain } });
      setSeoData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSeoLoading(false); }
  };

  const runMeta = async () => {
    if (!metaQ) return;
    setMetaLoading(true);
    try {
      const { data } = await axios.get(`${API}/marketing-apis/meta/ad-library`, { params: { q: metaQ, country: "US" } });
      setMetaData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setMetaLoading(false); }
  };

  const runGa = async () => {
    if (!gaQuery) return;
    setGaLoading(true);
    try {
      const { data } = await axios.get(`${API}/marketing-apis/google-ads/keywords`, { params: { query: gaQuery, location: gaLocation } });
      setGaData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setGaLoading(false); }
  };

  return (
    <div data-testid="marketing-apis-page" className="min-h-screen flex" style={{ background: "var(--al-bg)" }}>
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <header className="h-14 border-b border-[var(--al-border)] flex items-center justify-between px-6 bg-[var(--al-card)]">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="text-[12px] text-[var(--al-text-muted)] hidden md:inline">·</span>
            <h1 className="text-[14px] font-bold tracking-tight">{t.title}</h1>
          </div>
          <div className="flex items-center gap-2"><LangToggle /><ThemeToggle /></div>
        </header>

        <div className="p-6 space-y-5 max-w-[1480px] w-full mx-auto">
          <div>
            <h2 className="text-[22px] font-extrabold tracking-tight">{t.title}</h2>
            <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1">{t.subtitle}</p>
          </div>

          {/* DataForSEO config form — Iter47 lets users plug their key inline.
              `id` lets the clickable status rows scroll-to-anchor this card. */}
          <div id="dataforseo-config">
            <DataForSEOConfigCard
              lang={lang}
              connected={!!(status?.seo?.dataforseo)}
              onChanged={refreshStatus}
            />
          </div>

          {status && (
            <div className="al-card p-4" data-testid="mkt-status-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--al-text-muted)]">{t.status_title}</div>
                <div className="text-[10.5px] text-[var(--al-text-muted)] italic hidden sm:block">
                  {lang === "pt" ? "Clique em cada item para configurar" : "Click any row to configure"}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-[11.5px]">
                <div>
                  <strong>SEO</strong>
                  <ClickableIntegrationRow providerKey="dataforseo"   label="DataForSEO" connected={status.seo?.dataforseo} t={t} lang={lang} navigate={navigate} />
                  <ClickableIntegrationRow providerKey="semrush"      label="Semrush"    connected={status.seo?.semrush}     t={t} lang={lang} navigate={navigate} />
                  <ClickableIntegrationRow providerKey="ahrefs"       label="Ahrefs"     connected={status.seo?.ahrefs}      t={t} lang={lang} navigate={navigate} />
                  <ClickableIntegrationRow providerKey="gsc"          label="GSC"        connected={status.seo?.gsc}         t={t} lang={lang} navigate={navigate} />
                </div>
                <div>
                  <strong>Meta Ads</strong>
                  <ClickableIntegrationRow providerKey="meta_graph_api"         label="Graph API"          connected={status.meta_ads?.graph_api}       t={t} lang={lang} navigate={navigate} />
                  <ClickableIntegrationRow providerKey="meta_ad_library_apify"  label="Ad Library (Apify)" connected={status.meta_ads?.ad_library_apify} t={t} lang={lang} navigate={navigate} />
                </div>
                <div>
                  <strong>Google Ads</strong>
                  <ClickableIntegrationRow providerKey="google_ads_dataforseo"        label="DataForSEO"      connected={status.google_ads?.dataforseo}       t={t} lang={lang} navigate={navigate} />
                  <ClickableIntegrationRow providerKey="google_ads_developer_token"   label="Developer token" connected={status.google_ads?.developer_token}  t={t} lang={lang} navigate={navigate} />
                  <ClickableIntegrationRow providerKey="google_ads_refresh_token"     label="Refresh token"   connected={status.google_ads?.refresh_token}    t={t} lang={lang} navigate={navigate} />
                </div>
                <div>
                  <strong>WhatsApp</strong>
                  <ClickableIntegrationRow providerKey="wati"        label="Wati"        connected={status.whatsapp?.wati}        t={t} lang={lang} navigate={navigate} />
                  <ClickableIntegrationRow providerKey="respond_io"  label="respond.io"  connected={status.whatsapp?.respond_io}  t={t} lang={lang} navigate={navigate} />
                  <ClickableIntegrationRow providerKey="sleekflow"   label="SleekFlow"   connected={status.whatsapp?.sleekflow}   t={t} lang={lang} navigate={navigate} />
                  <ClickableIntegrationRow providerKey="blip_go"     label="Blip Go"     connected={status.whatsapp?.blip_go}     t={t} lang={lang} navigate={navigate} />
                </div>
              </div>
            </div>
          )}

          <VerticalDemandStrip lang={lang} />

          <div className="grid lg:grid-cols-3 gap-5">
            {/* SEO */}
            <div className="al-card p-5" data-testid="mkt-seo-card">
              <div className="flex items-center gap-2 mb-1"><Gauge className="w-4 h-4 text-[#8B5CF6]" /><h3 className="text-[14px] font-bold">{t.card_seo_title}</h3></div>
              <p className="text-[11.5px] text-[var(--al-text-muted)] mb-3">{t.card_seo_desc}</p>
              <div className="flex gap-2">
                <input value={seoDomain} onChange={(e) => setSeoDomain(e.target.value)} placeholder={t.domain_ph}
                  data-testid="mkt-seo-domain" className="al-input h-9 flex-1 text-[12px]" />
                <button type="button" onClick={runSeo} disabled={seoLoading || !seoDomain}
                  data-testid="mkt-seo-run"
                  className="h-9 px-3 rounded-md bg-[#8B5CF6] text-white text-[11.5px] font-bold hover:bg-[#7C3AED] disabled:opacity-50 inline-flex items-center gap-1">
                  {seoLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} {t.run}
                </button>
              </div>
              {seoData && (
                <div className="mt-3 space-y-2 text-[12px]" data-testid="mkt-seo-result">
                  {seoData.is_mocked && <div className="text-[10.5px] italic text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">{t.mock_notice}</div>}
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <Stat label={t.seo_da} value={seoData.domain_authority} color="#8B5CF6" />
                    <Stat label={t.seo_local} value={seoData.local_seo_score} color="#10B981" suffix="/100" />
                    <Stat label={t.seo_backlinks} value={seoData.backlinks?.toLocaleString("en-US")} color="#0EA5E9" />
                    <Stat label={t.seo_kws} value={seoData.organic_keywords?.toLocaleString("en-US")} color="#F59E0B" />
                  </div>
                  <div>
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--al-text-muted)] mt-2">{t.seo_issues}</div>
                    <ul className="text-[11.5px] space-y-1 mt-1">
                      {(seoData.top_issues || []).map((is) => <li key={is} className="flex items-start gap-1.5"><X className="w-3 h-3 text-rose-500 mt-0.5 shrink-0" /> {is}</li>)}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Meta */}
            <div className="al-card p-5" data-testid="mkt-meta-card">
              <div className="flex items-center gap-2 mb-1"><Facebook className="w-4 h-4 text-[#1877F2]" /><h3 className="text-[14px] font-bold">{t.card_meta_title}</h3></div>
              <p className="text-[11.5px] text-[var(--al-text-muted)] mb-3">{t.card_meta_desc}</p>
              <div className="flex gap-2">
                <input value={metaQ} onChange={(e) => setMetaQ(e.target.value)} placeholder={t.query_ph}
                  data-testid="mkt-meta-query" className="al-input h-9 flex-1 text-[12px]" />
                <button type="button" onClick={runMeta} disabled={metaLoading || !metaQ}
                  data-testid="mkt-meta-run"
                  className="h-9 px-3 rounded-md bg-[#1877F2] text-white text-[11.5px] font-bold hover:bg-[#0E5FC5] disabled:opacity-50 inline-flex items-center gap-1">
                  {metaLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Facebook className="w-3.5 h-3.5" />} {t.run}
                </button>
              </div>
              {metaData && (
                <div className="mt-3 text-[12px]" data-testid="mkt-meta-result">
                  {metaData.is_mocked && <div className="text-[10.5px] italic text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">{t.mock_notice}</div>}
                  <div className={`p-3 rounded-lg ${metaData.has_active_ads ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
                    <div className={`font-bold ${metaData.has_active_ads ? "text-emerald-700" : "text-rose-700"} flex items-center gap-1.5`}>
                      {metaData.has_active_ads ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      {metaData.has_active_ads ? `${metaData.active_count} ${t.meta_active_yes.toLowerCase()}` : t.meta_active_no}
                    </div>
                    {!metaData.has_active_ads && (<div className="text-[11px] mt-1 text-rose-700 font-semibold">→ {t.meta_open}</div>)}
                  </div>
                  {(() => {
                    // Filter out samples that have no useful content (old cache
                    // entries or shallow Apify responses sometimes stored empty items).
                    const validSamples = (metaData.sample_ads || [])
                      .filter((ad) => (ad.headline && ad.headline.trim()) || (ad.primary_text && ad.primary_text.trim()));
                    if (!validSamples.length) return null;
                    return (
                      <div className="mt-3">
                        <div className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--al-text-muted)] mb-1">{t.meta_sample}</div>
                        <ul className="space-y-1.5">
                          {validSamples.map((ad, i) => {
                            // Stable key: prefer headline (sample ads from
                            // Apify don't carry a unique id). Fall back to
                            // truncated primary_text + index disambiguator
                            // so identical headlines don't collide.
                            const key = ad.headline?.trim()
                              || `${(ad.primary_text || "").slice(0, 40)}#${i}`;
                            return (
                            <li key={key} className="text-[11.5px] border border-[var(--al-border)] rounded p-2">
                              <strong>{ad.headline || ad.primary_text.slice(0, 60)}</strong> <span className="text-[10px] uppercase text-[var(--al-text-muted)] ml-1">[{ad.platform}]</span>
                              {ad.primary_text && ad.primary_text !== ad.headline && <div className="text-[11px] text-[var(--al-text-muted)] mt-0.5 line-clamp-2">{ad.primary_text}</div>}
                            </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Google Ads */}
            <div className="al-card p-5" data-testid="mkt-ga-card">
              <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-[#34A853]" /><h3 className="text-[14px] font-bold">{t.card_ga_title}</h3></div>
              <p className="text-[11.5px] text-[var(--al-text-muted)] mb-3">{t.card_ga_desc}</p>
              <div className="grid grid-cols-2 gap-2">
                <input value={gaQuery} onChange={(e) => setGaQuery(e.target.value)} placeholder={t.query_ph}
                  data-testid="mkt-ga-query" className="al-input h-9 text-[12px]" />
                <input value={gaLocation} onChange={(e) => setGaLocation(e.target.value)} placeholder={t.location_ph}
                  data-testid="mkt-ga-location" className="al-input h-9 text-[12px]" />
              </div>
              <button type="button" onClick={runGa} disabled={gaLoading || !gaQuery}
                data-testid="mkt-ga-run"
                className="mt-2 w-full h-9 px-3 rounded-md bg-[#34A853] text-white text-[11.5px] font-bold hover:bg-[#2D8F43] disabled:opacity-50 inline-flex items-center justify-center gap-1">
                {gaLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Target className="w-3.5 h-3.5" />} {t.run}
              </button>
              {gaData && (
                <div className="mt-3 text-[12px]" data-testid="mkt-ga-result">
                  {gaData.is_mocked && <div className="text-[10.5px] italic text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">{t.mock_notice}</div>}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <Stat label={t.ga_search_vol} value={gaData.totals?.monthly_searches?.toLocaleString("en-US")} color="#34A853" />
                    <Stat label={t.ga_cpc} value={`$${gaData.totals?.avg_cpc_usd}`} color="#0EA5E9" />
                    <Stat label={t.ga_opp} value={`$${Math.round(gaData.totals?.est_monthly_traffic_value_usd).toLocaleString("en-US")}`} color="#F59E0B" />
                  </div>
                  <table className="w-full mt-3 text-[11px]">
                    <thead>
                      <tr className="text-left text-[var(--al-text-muted)] border-b border-[var(--al-border)]">
                        <th className="py-1">{t.ga_table_kw}</th>
                        <th className="py-1 text-right">{t.ga_table_vol}</th>
                        <th className="py-1 text-right">{t.ga_table_comp}</th>
                        <th className="py-1 text-right">{t.ga_table_cpc}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(gaData.keywords || []).map((k) => (
                        <tr key={k.keyword} className="border-b border-[var(--al-border)]/50">
                          <td className="py-1 truncate max-w-[140px]" title={k.keyword}>{k.keyword}</td>
                          <td className="py-1 text-right font-mono">{k.avg_monthly_searches.toLocaleString("en-US")}</td>
                          <td className="py-1 text-right">
                            <span className={`text-[9.5px] px-1 rounded ${k.competition === "HIGH" ? "bg-rose-100 text-rose-700" : k.competition === "MEDIUM" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{k.competition}</span>
                          </td>
                          <td className="py-1 text-right font-mono">${k.cpc_low} – ${k.cpc_high}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color = "#8B5CF6", suffix = "" }) {
  return (
    <div className="rounded-md border border-[var(--al-border)] p-2">
      <div className="text-[9.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-bold">{label}</div>
      <div className="text-[15px] font-extrabold mt-0.5 tabular-nums" style={{ color }}>{value}{suffix}</div>
    </div>
  );
}

/**
 * VerticalDemandStrip — inline pre-click badges showing search volume per
 * vertical BEFORE the user runs a deep audit. Helps prioritize which niche
 * to pitch today. Uses the batch endpoint to hit the backend only once.
 */
const VERTICAL_TERMS = [
  { key: "roofing", label_en: "Roofing", label_pt: "Roofing", label_es: "Techos" },
  { key: "hvac", label_en: "HVAC", label_pt: "HVAC", label_es: "HVAC" },
  { key: "solar", label_en: "Solar", label_pt: "Solar", label_es: "Solar" },
  { key: "plumbing", label_en: "Plumbing", label_pt: "Encanamento", label_es: "Plomería" },
  { key: "general contractor", label_en: "General Contractor", label_pt: "Construção Geral", label_es: "Contratista Gen." },
  { key: "electrician", label_en: "Electrical", label_pt: "Elétrica", label_es: "Eléctricos" },
];
const TIER_COLORS = {
  "Very High": { bg: "#FEE2E2", fg: "#B91C1C", dot: "#DC2626" },
  "High":      { bg: "#FEF3C7", fg: "#B45309", dot: "#F59E0B" },
  "Medium":    { bg: "#DBEAFE", fg: "#1D4ED8", dot: "#3B82F6" },
  "Low":       { bg: "#DCFCE7", fg: "#15803D", dot: "#10B981" },
  "Very Low":  { bg: "#F3F4F6", fg: "#6B7280", dot: "#9CA3AF" },
};

function VerticalDemandStrip({ lang }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const qs = VERTICAL_TERMS.map((v) => v.key).join(",");
    axios.get(`${API}/marketing-apis/keyword-volume-batch`, { params: { queries: qs, location: "United States" } })
      .then(({ data }) => setData(data.items || {}))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, []);

  const labelKey = `label_${lang === "pt" ? "pt" : lang === "es" ? "es" : "en"}`;
  const heading = lang === "pt" ? "Demanda por vertical (mock · busca mensal)"
    : lang === "es" ? "Demanda por vertical (mock · búsquedas/mes)"
    : "Demand per vertical (mock · monthly searches)";
  const hint = lang === "pt" ? "Priorize o alvo com maior volume + menor concorrência antes de abrir a auditoria."
    : lang === "es" ? "Prioriza el alvo con más volumen + menos competencia antes de abrir la auditoría."
    : "Prioritize the highest-volume + lowest-competition niche before you open the audit.";

  return (
    <div className="al-card p-4" data-testid="mkt-demand-strip">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-[13.5px] font-bold">{heading}</h3>
          <p className="text-[11px] text-[var(--al-text-muted)] mt-0.5">{hint}</p>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-[var(--al-text-muted)]" />}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {VERTICAL_TERMS.map((v) => {
          const d = data[v.key];
          const tier = d?.demand_tier || (loading ? "…" : "—");
          const colors = TIER_COLORS[tier] || TIER_COLORS.Low;
          return (
            <a
              key={v.key}
              href={`/v/${v.key === "general contractor" ? "gc" : v.key === "electrician" ? "elect" : v.key}`}
              data-testid={`mkt-demand-card-${v.key}`}
              className="block p-3 rounded-lg border transition-all hover:shadow-sm hover:-translate-y-0.5"
              style={{ borderColor: "var(--al-border)", background: colors.bg }}
            >
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: colors.dot }} />
                <span className="text-[10.5px] font-bold uppercase tracking-wider" style={{ color: colors.fg }}>{tier}</span>
              </div>
              <div className="mt-1 text-[13px] font-extrabold tracking-tight" style={{ color: "var(--al-text)" }}>{v[labelKey]}</div>
              {d && (
                <div className="mt-0.5 text-[10.5px] font-semibold tabular-nums" style={{ color: colors.fg }}>
                  {d.monthly_searches >= 1000 ? `${Math.round(d.monthly_searches / 1000)}k` : d.monthly_searches}/mo · ${d.avg_cpc_usd} CPC
                </div>
              )}
            </a>
          );
        })}
      </div>
    </div>
  );
}
